import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, MapPin } from "lucide-react";
import Reveal from "../components/Reveal";
import { PROJECTS, scrollToId } from "../data/projects";

export default function ProjectDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const idx = PROJECTS.findIndex((p) => p.slug === slug);
  const project = PROJECTS[idx];
  const next = PROJECTS[(idx + 1) % PROJECTS.length];

  useEffect(() => {
    window.__lenis?.scrollTo(0, { immediate: true }) ?? window.scrollTo(0, 0);
  }, [slug]);

  if (!project) {
    return (
      <main data-testid="project-not-found" className="flex min-h-screen flex-col items-center justify-center gap-6 px-6 text-center">
        <h1 className="font-display text-4xl font-semibold text-cream">Project not found</h1>
        <Link to="/" data-testid="project-not-found-back" className="font-mono text-xs uppercase tracking-[0.2em] text-copper">
          ← Back to home
        </Link>
      </main>
    );
  }

  const meta = [
    { label: "Location", value: project.location },
    { label: "Year", value: project.year },
    { label: "Duration", value: project.duration },
    { label: "Investment", value: project.investment },
  ];

  return (
    <main data-testid="project-detail-page" className="pt-[72px]">
      <section className="relative flex min-h-[62vh] items-end overflow-hidden">
        <motion.img
          initial={{ scale: 1.08 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
          src={project.image_url}
          alt={project.title}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/45 to-ink/30" />
        <div className="relative mx-auto w-full max-w-7xl px-4 pb-14 sm:px-8">
          <Link
            to="/"
            data-testid="project-back-link"
            onClick={(e) => {
              e.preventDefault();
              navigate("/");
              setTimeout(() => scrollToId("portfolio"), 400);
            }}
            className="mb-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-cream/70 transition-colors hover:text-copper"
          >
            <ArrowLeft size={14} /> Portfolio
          </Link>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="font-mono text-xs uppercase tracking-[0.3em] text-copper"
          >
            {project.category_label}
          </motion.p>
          <div className="overflow-hidden">
            <motion.h1
              data-testid="project-title"
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ delay: 0.3, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="mt-3 max-w-4xl font-display text-4xl font-bold tracking-tight text-cream sm:text-6xl"
            >
              {project.title}
            </motion.h1>
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-4 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-cream/60"
          >
            <MapPin size={13} className="text-copper" /> {project.location}
          </motion.p>
        </div>
      </section>

      <section className="border-b border-line bg-surface/40">
        <div className="mx-auto grid max-w-7xl grid-cols-2 lg:grid-cols-4">
          {meta.map((m, i) => (
            <div
              key={m.label}
              data-testid={`project-meta-${m.label.toLowerCase()}`}
              className={`border-line px-6 py-8 ${i % 2 === 1 ? "border-l" : ""} ${i > 1 ? "border-t lg:border-t-0" : ""} ${i > 0 ? "lg:border-l" : ""}`}
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-copper">{m.label}</p>
              <p className="mt-2 font-display text-xl font-semibold text-cream sm:text-2xl">{m.value}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:py-28">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-5">
          <Reveal className="lg:col-span-3">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-copper">The brief</p>
            <p data-testid="project-summary" className="mt-5 font-display text-2xl font-medium leading-snug tracking-tight text-cream sm:text-3xl">
              {project.summary}
            </p>
            <div className="mt-12 grid grid-cols-1 gap-10 sm:grid-cols-2">
              <div data-testid="project-challenge">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-copper">Challenge</p>
                <p className="mt-4 text-sm leading-relaxed text-cream/65">{project.challenge}</p>
              </div>
              <div data-testid="project-solution">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-copper">Solution</p>
                <p className="mt-4 text-sm leading-relaxed text-cream/65">{project.solution}</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-2">
            <div className="rounded-2xl border border-line bg-surface p-8">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-copper">Material & spec sheet</p>
              <ul data-testid="project-specs" className="mt-6 space-y-4">
                {project.specs.map((s) => (
                  <li key={s} className="flex items-center gap-3 text-sm text-cream/75">
                    <span className="h-1.5 w-1.5 rotate-45 bg-copper" /> {s}
                  </li>
                ))}
              </ul>
              <Link
                to="/"
                data-testid="project-enquire-cta"
                onClick={() => setTimeout(() => scrollToId("contact"), 400)}
                className="group mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-copper px-6 py-4 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-ink transition-colors duration-300 hover:bg-copper-hover"
              >
                Start a similar project
                <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {project.gallery && project.gallery.length > 0 && (
        <section data-testid="project-gallery" className="mx-auto max-w-7xl px-4 pb-20 sm:px-8 lg:pb-28">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-copper">Job photos</p>
            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {project.gallery.map((src, i) => (
                <div
                  key={src}
                  data-testid={`project-gallery-image-${i}`}
                  className="group aspect-[3/4] overflow-hidden rounded-2xl border border-line"
                >
                  <img
                    src={src}
                    alt={`${project.title} — job photo ${i + 1}`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
              ))}
            </div>
          </Reveal>
        </section>
      )}

      {next && (
        <Link
          to={`/projects/${next.slug}`}
          data-testid="project-next-link"
          className="group block border-t border-line bg-surface/40 transition-colors hover:bg-surface"
        >
          <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-14 sm:px-8">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-copper">Next project</p>
              <p className="mt-3 font-display text-3xl font-semibold tracking-tight text-cream transition-colors group-hover:text-copper sm:text-4xl">
                {next.title}
              </p>
            </div>
            <ArrowRight size={32} className="text-cream/40 transition-all duration-300 group-hover:translate-x-2 group-hover:text-copper" />
          </div>
        </Link>
      )}
    </main>
  );
}
